/**
 * Connections: one small square button with a status dot wherever something's state matters
 * (this device's sync, the Mac running the assistant). Tap it for the Connections pop-up — every
 * connection with its own dot and a word or two: sync, the Mac(s), Google Calendar, connected
 * apps — instead of status text sprinkled around the app.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Sheet } from './Sheet'
import { useSyncStatus } from '../store/hooks'
import { openSheet, replaceTo } from '../lib/router'
import { chatApi, type ChatState } from '../lib/chat'
import { IconChevronRight } from './Icons'

export type DotState = 'ok' | 'busy' | 'off' | 'problem'

const COLORS: Record<DotState, string> = { ok: 'bg-ok', busy: 'bg-warn', off: 'bg-line-2', problem: 'bg-danger' }

export function StatusDot({ state, className = '' }: { state: DotState; className?: string }) {
  return <span className={`inline-block h-2 w-2 flex-none rounded-full ${COLORS[state]} ${className}`} aria-hidden="true" />
}

export function syncState(s: ReturnType<typeof useSyncStatus>): { state: DotState; label: string; detail: string } {
  if (!s.online) return { state: 'off', label: 'Offline', detail: 'Saved on this device; syncs when you’re back online' }
  if (!s.connected) return { state: 'busy', label: 'Connecting…', detail: 'Saved on this device; syncs as soon as it connects' }
  return { state: 'ok', label: 'Synced', detail: 'Every change syncs right away' }
}

/** The square dot button. Without a `state`, it shows this device's sync. */
/** The status dot that opens Connections. `className`: its size and corners (a small square unless given). */
export function StatusButton({ state, label, className = 'h-7 w-7 rounded-md' }: { state?: DotState; label?: string; className?: string }) {
  const sync = syncState(useSyncStatus())
  const st = state ?? sync.state
  return (
    <button
      type="button"
      onClick={() => openSheet('connections')}
      aria-label={`Connections: ${label ?? sync.label}`}
      title={label ?? sync.label}
      data-status={st}
      className={`flex flex-none items-center justify-center border border-line-2 hover:bg-surface-2 ${className}`}
    >
      <StatusDot state={st} />
    </button>
  )
}

interface Gcal {
  connected: boolean
  email?: string
  lastError?: string
  lastPullAt?: number
}

export default function ConnectionsSheet() {
  const sync = syncState(useSyncStatus())
  const [chat, setChat] = useState<ChatState | null>(null)
  const [gcal, setGcal] = useState<Gcal | null>(null)
  const [apps, setApps] = useState<{ grants: Array<{ clientName: string }>; pats: Array<{ label: string }> } | null>(null)
  useEffect(() => {
    void chatApi<ChatState>(`/chat?c=shared&since=${Date.now()}`)
      .then(setChat)
      .catch(() => setChat(null))
    void chatApi<Gcal>('/gcal')
      .then(setGcal)
      .catch(() => setGcal(null))
    void Promise.all([chatApi<Array<{ clientName: string }>>('/grants'), chatApi<Array<{ label: string }>>('/pats')])
      .then(([grants, pats]) => setApps({ grants, pats }))
      .catch(() => setApps(null))
  }, [])

  const macs = chat ? [...chat.mine, ...(chat.runner && !chat.mine.some((r) => r.id === chat.runner!.id) ? [chat.runner] : [])] : []
  return (
    <Sheet title="Connections">
      <div className="-mx-2" data-connections>
        <Line state={sync.state} title="This device" detail={sync.label === 'Synced' ? 'Synced' : `${sync.label} — ${sync.detail}`} onClick={() => replaceTo('/settings/diagnostics')} />
        {macs.map((r) => (
          <Line
            key={r.id}
            state={r.paused || !r.online ? 'off' : r.problem ? 'problem' : r.busy ? 'busy' : 'ok'}
            title={r.name}
            detail={`${r.paused ? 'Paused' : !r.online ? 'Asleep or offline' : r.problem ? r.problem : r.busy ? 'Working on a message' : 'Online'} · ${r.owner}’s assistant${chat?.sharing && r.ownerId === chat.runner?.ownerId ? ', shared' : ''}`}
            onClick={() => replaceTo('/settings/assistant')}
          />
        ))}
        {chat && !macs.length && <Line state="off" title="Assistant" detail="No Mac connected" onClick={() => replaceTo('/settings/assistant')} />}
        {gcal && (
          <Line
            state={!gcal.connected ? 'off' : gcal.lastError ? 'problem' : 'ok'}
            title="Google Calendar"
            detail={!gcal.connected ? 'Not connected' : gcal.lastError ? 'Last sync had a problem' : (gcal.email ?? 'Connected')}
            onClick={() => replaceTo('/settings/calendar')}
          />
        )}
        {apps && (
          <Line
            state={apps.grants.length + apps.pats.length ? 'ok' : 'off'}
            title="Connected apps"
            detail={
              apps.grants.length + apps.pats.length
                ? [...apps.grants.map((g) => g.clientName), ...apps.pats.map((p) => p.label)].slice(0, 3).join(', ') + (apps.grants.length + apps.pats.length > 3 ? '…' : '')
                : 'None'
            }
            onClick={() => replaceTo('/settings/assistant')}
          />
        )}
      </div>
    </Sheet>
  )
}

function Line({ state, title, detail, onClick }: { state: DotState; title: string; detail: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex min-h-12 w-full items-center gap-3 rounded-lg px-2 text-left hover:bg-surface-2" data-connection={title}>
      <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md border border-line-2">
        <StatusDot state={state} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[0.9375rem]">{title}</span>
        <span className="block truncate text-xs text-muted">{detail}</span>
      </span>
      <IconChevronRight size={16} className="flex-none text-faint" />
    </button>
  )
}
